import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Meta, Title } from '@angular/platform-browser';
import { ToastService } from '../../shared/services';
import { PublicBookingService } from '../../services/bookings/public-booking.service';
import {
  BusinessPublicInfo,
  PublicAgendaEvent,
  PublicScheduleBlock,
} from '../../shared/interfaces';
import { forkJoin } from 'rxjs';
import { STATUS_DEF_MAP } from '../../shared/api/eventStatus.token';
import { BusinessProfileHeaderComponent } from './components/business-profile-header/business-profile-header';
import { MicrositeActionsBarComponent } from './components/microsite-actions-bar/microsite-actions-bar';
import { BookingWizardComponent, BookingWizardData } from './components/booking-wizard/booking-wizard';
import { ServiceCardComponent } from './components/service-card/service-card';
import { getReadableInk } from './microsite-colors';
import { DayAvailability, AvailabilityLevel } from './components/availability-calendar/availability-calendar';
@Component({
  selector: 'app-public-booking',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    BusinessProfileHeaderComponent,
    MicrositeActionsBarComponent,
    BookingWizardComponent,
    ServiceCardComponent,
  ],
  templateUrl: './public-booking.html',
  styleUrls: ['./microsite-tokens.css', './public-booking.css'],
  host: {
    '[style.--ms-brand]': 'getPrimaryColor()',
    '[style.--ms-brand-ink]': 'getBrandInk()',
  },
})
export class PublicBooking implements OnInit {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private bookingService = inject(PublicBookingService);
  private toastService = inject(ToastService);
  private destroyRef = inject(DestroyRef);
  private statusDefs = inject(STATUS_DEF_MAP);
  private meta = inject(Meta);
  private title = inject(Title);

  business = signal<BusinessPublicInfo | null>(null);
  services = signal<any[]>([]);
  professionals = signal<any[]>([]);
  businessSchedule = signal<Array<{
    businessScheduleId: string;
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    isActive: boolean;
  }>>([]);
  blockedEvents = signal<PublicAgendaEvent[]>([]);
  isLoadingBusiness = signal(true);
  isLoadingServices = signal(false);
  isLoadingProfessionals = signal(false);
  isLoadingSchedule = signal(false);
  isLoadingTimeSlots = signal(false);
  isSubmitting = signal(false);
  businessNotFound = signal(false);
  slug = signal<string | null>(null);
  businessId = signal<string | null>(null);
  showWizard = signal<boolean>(false);
  initialServiceId = signal<string | null>(null); // Service picked from the landing menu
  selectedDate = signal<string | null>(null); // Track selected date from wizard
  selectedServiceId = signal<string | null>(null); // Track selected service ID from wizard
  selectedProfessionalId = signal<string | null>(null); // Track selected professional ID from wizard
  monthEvents = signal<PublicAgendaEvent[]>([]); // Events for the entire visible month
  scheduleBlocks = signal<PublicScheduleBlock[]>([]); // Agenda blocks for the visible month

  // Blocks expanded into one busy range per day, in local business time
  blockEvents = computed<PublicAgendaEvent[]>(() =>
    this.scheduleBlocks().flatMap((block) => this.expandBlock(block))
  );
  isLoadingMonthAvailability = signal(false);
  currentCalendarMonth = signal<{ year: number; month: number }>({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  });

  // Computed per-day availability for the calendar.
  // Uses the same slot builder as the time step, so a day's color matches the hours shown for it.
  dayAvailabilities = computed<DayAvailability[]>(() => {
    const schedule = this.businessSchedule();
    const events = [...this.monthEvents(), ...this.blockEvents()];
    const { year, month } = this.currentCalendarMonth();
    const serviceId = this.selectedServiceId();
    const professionalId = this.selectedProfessionalId();

    if (!schedule.length) return [];

    const service = (serviceId ? this.services().find(s => s.serviceId === serviceId) : null)
      ?? { durationMinutes: 60 };

    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const availabilities: DayAvailability[] = [];

    for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
      const dateStr = this.formatDate(d);
      const hasSchedule = schedule.some(r => r.dayOfWeek === d.getDay() && r.isActive);

      if (!hasSchedule || d < today) {
        availabilities.push({
          date: dateStr,
          totalSlots: 0,
          availableSlots: 0,
          level: d < today ? 'unknown' : 'none',
        });
        continue;
      }

      // Capacity of the day without bookings or blocks vs. what is actually free
      const totalSlots = this.buildBasicTimeOptions(dateStr, service, [], serviceId, professionalId).length;
      const availableSlots = this.buildBasicTimeOptions(dateStr, service, events, serviceId, professionalId).length;

      availabilities.push({
        date: dateStr,
        totalSlots,
        availableSlots,
        level: this.getAvailabilityLevel(availableSlots, totalSlots),
      });
    }

    return availabilities;
  });

  // Computed time options based on schedule, blocks, and selected date
  timeOptions = computed(() => {
    const selectedDate = this.selectedDate(); // Use signal instead of form value
    const serviceId = this.selectedServiceId(); // Use signal from wizard
    const professionalId = this.selectedProfessionalId(); // Use signal from wizard
    const events = [...this.blockedEvents(), ...this.blockEvents()];
    const schedule = this.businessSchedule();
    const allServices = this.services();

    // Find service by ID
    const service = serviceId ? allServices.find(s => s.serviceId === serviceId) : null;

    if (!selectedDate || !service) {
      return [];
    }

    if (schedule.length === 0) {
      return [];
    }

    // Build time options - this will recalculate when selectedDate, serviceId, professionalId, events, or schedule change
    const slots = this.buildBasicTimeOptions(selectedDate, service, events, serviceId, professionalId);
    return slots;
  });

  form: FormGroup = this.fb.group({
    serviceId: ['', Validators.required],
    professionalId: [''], // Optional
    date: ['', Validators.required],
    time: ['', Validators.required],
    clientName: ['', [Validators.required, Validators.minLength(2)]],
    phone: [null, Validators.required], // Combined country + phone
    notes: [''],
  });

  selectedService = computed<any | null>(() => {
    const sId = this.form.get('serviceId')?.value;
    if (!sId) return null;
    return this.services().find((s) => s.serviceId === sId) ?? null;
  });

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (params) => {
        const slug = params.get('slug');
        if (!slug) return;
        this.slug.set(slug);
        this.loadBusiness(slug);
      },
    });

    // Load professionals when service changes
    this.form
      .get('serviceId')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((serviceId) => {
        if (serviceId && this.slug()) {
          this.loadProfessionalsForService(serviceId);
        } else {
          this.professionals.set([]);
        }
      });

    this.form
      .get('date')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (value && this.businessId()) {
          this.onDateChange(value);
        }
      });
  }

  get serviceControl(): FormControl {
    return this.form.get('serviceId') as FormControl;
  }

  private loadBusiness(slug: string): void {
    this.isLoadingBusiness.set(true);
    this.bookingService.getBusinessBySlug(slug).subscribe({
      next: (business) => {
        this.business.set(business);
        this.businessId.set(business.businessId);
        this.businessNotFound.set(false);

        // Update meta tags for social sharing
        this.updateMetaTags(business);

        // Load services and schedule for the business
        this.loadServices(slug);
        this.loadBusinessSchedule(slug);

        // Load month events for the availability calendar
        const now = new Date();
        this.loadMonthEvents(now.getFullYear(), now.getMonth() + 1);

        // Set initial date to today, but if it's a weekend, move to next Monday
        let initialDate = new Date();
        const dayOfWeek = initialDate.getDay();
        if (dayOfWeek === 0) {
          // Sunday - move to Monday
          initialDate.setDate(initialDate.getDate() + 1);
        } else if (dayOfWeek === 6) {
          // Saturday - move to Monday
          initialDate.setDate(initialDate.getDate() + 2);
        }

              const dateStr = this.formatDate(initialDate);
              this.form.patchValue({ date: dateStr }, { emitEvent: false });
              this.selectedDate.set(dateStr); // Set initial date in signal

              // Load events for initial date
              const { startUtc, endUtc } = this.getDayRangeUtc(dateStr);
              // Note: professionalId may not be set yet at this point, but will be set after professionals load
              // We'll reload events when professional is selected
              this.bookingService
                .getBusinessEvents(slug, startUtc, endUtc, null)
                .subscribe({
                  next: (events) => {
                    // Store all events initially, will be filtered when professional is selected
                    this.blockedEvents.set(events);
                  },
                  error: () => this.blockedEvents.set([]),
                });

              this.isLoadingBusiness.set(false);
      },
      error: () => {
        this.isLoadingBusiness.set(false);
        this.businessNotFound.set(true);
        this.toastService.show(
          'No pudimos cargar la información del negocio. Verifica el enlace recibido.',
          'warning'
        );
      },
    });
  }

  private loadServices(slug: string): void {
    this.isLoadingServices.set(true);
    this.bookingService.getBusinessServices(slug).subscribe({
      next: (services) => {
        this.services.set(services);
        if (services.length === 1) {
          const serviceId = services[0].serviceId;
          this.form.patchValue({ serviceId });
          this.selectedServiceId.set(serviceId); // Update signal for timeOptions
        }
        this.isLoadingServices.set(false);
      },
      error: () => {
        this.isLoadingServices.set(false);
        this.toastService.show('Error al cargar los servicios', 'warning');
      },
    });
  }

  onServiceSelected(serviceId: string): void {
    // Update the selected service ID signal - this will trigger timeOptions recomputation
    this.selectedServiceId.set(serviceId);
    // Also update the form for backward compatibility
    this.form.patchValue({ serviceId });
    // Load professionals for the service
    this.loadProfessionalsForService(serviceId);
  }

  onProfessionalSelected(professionalId: string | null): void {
    // Update the selected professional ID signal - this will trigger timeOptions recomputation
    this.selectedProfessionalId.set(professionalId);
    // Also update the form for backward compatibility
    this.form.patchValue({ professionalId: professionalId || '' });

    // Reload month events for calendar availability
    const { year, month } = this.currentCalendarMonth();
    this.loadMonthEvents(year, month);

    // Reload events for the current date when professional changes
    // This ensures we have the latest events for the selected professional
    if (this.selectedDate() && this.slug()) {
      const { startUtc, endUtc } = this.getDayRangeUtc(this.selectedDate()!);
      this.isLoadingTimeSlots.set(true);

      this.bookingService
        .getBusinessEvents(this.slug()!, startUtc, endUtc, professionalId)
        .subscribe({
          next: (events) => {
            // Backend now filters by professional, but we still need to include events without professional (blocks)
            let filteredEvents = events;

            if (professionalId) {
              const normalizeId = (id: string | null | undefined): string | null => {
                if (!id) return null;
                return id.trim().toLowerCase().replace(/-/g, '');
              };

              const normalizedSelectedId = normalizeId(professionalId);
              // Include events for the selected professional OR events without professional (blocks)
              filteredEvents = events.filter(event => {
                const eventProfId = normalizeId(event.professionalId);
                return !eventProfId || eventProfId === normalizedSelectedId;
              });
            }

            this.blockedEvents.set(filteredEvents);
            setTimeout(() => {
              this.isLoadingTimeSlots.set(false);
            }, 100);
          },
          error: () => {
            this.blockedEvents.set([]);
            this.isLoadingTimeSlots.set(false);
          },
        });
    }
  }

  loadProfessionalsForService(serviceId: string): void {
    if (!this.slug()) return;
    this.isLoadingProfessionals.set(true);
    this.bookingService.getProfessionalsByService(this.slug()!, serviceId).subscribe({
      next: (professionals) => {
        this.professionals.set(professionals);
        const currentProfessionalId = this.selectedProfessionalId();
        const isStillValid = !!currentProfessionalId
          && professionals.some((p) => p.userId === currentProfessionalId);
        if (!isStillValid) {
          this.selectedProfessionalId.set(null);
          this.form.patchValue({ professionalId: '' });

          if (this.selectedDate() && this.slug()) {
            const { startUtc, endUtc } = this.getDayRangeUtc(this.selectedDate()!);
            this.isLoadingTimeSlots.set(true);
            this.bookingService
              .getBusinessEvents(this.slug()!, startUtc, endUtc, null)
              .subscribe({
                next: (events) => {
                  this.blockedEvents.set(events);
                  setTimeout(() => {
                    this.isLoadingTimeSlots.set(false);
                  }, 100);
                },
                error: () => {
                  this.blockedEvents.set([]);
                  this.isLoadingTimeSlots.set(false);
                },
              });
          }
        }
        this.isLoadingProfessionals.set(false);
      },
      error: () => {
        this.isLoadingProfessionals.set(false);
        this.professionals.set([]);
      },
    });
  }

  private loadBusinessSchedule(slug: string): void {
    this.isLoadingSchedule.set(true);
    this.bookingService.getBusinessSchedule(slug).subscribe({
      next: (response: {
        businessId: string;
        timeRanges: Array<{
          businessScheduleId: string;
          dayOfWeek: number;
          startTime: string;
          endTime: string;
          isActive: boolean;
        }>;
      }) => {
        this.businessSchedule.set(response.timeRanges.filter((r: { isActive: boolean }) => r.isActive));
        this.isLoadingSchedule.set(false);
      },
      error: () => {
        this.isLoadingSchedule.set(false);
        this.businessSchedule.set([]);
      },
    });
  }


  /** Load bookings and agenda blocks for an entire month to calculate per-day availability */
  loadMonthEvents(year: number, month: number): void {
    if (!this.slug()) return;
    this.isLoadingMonthAvailability.set(true);
    this.currentCalendarMonth.set({ year, month });

    // Month boundaries in local time, so evening bookings on the last day are included
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const startUtc = firstDay.toISOString();
    const endUtc = new Date(year, month, 1).toISOString();
    const professionalId = this.selectedProfessionalId();

    forkJoin({
      events: this.bookingService.getBusinessEvents(this.slug()!, startUtc, endUtc, professionalId),
      blocks: this.bookingService.getBusinessBlocks(
        this.slug()!,
        this.formatDate(firstDay),
        this.formatDate(lastDay),
        professionalId
      ),
    }).subscribe({
      next: ({ events, blocks }) => {
        this.monthEvents.set(events);
        this.scheduleBlocks.set(blocks);
        this.isLoadingMonthAvailability.set(false);
      },
      error: () => {
        this.monthEvents.set([]);
        this.scheduleBlocks.set([]);
        this.isLoadingMonthAvailability.set(false);
      },
    });
  }

  /** Handle month change from the availability calendar */
  onCalendarMonthChanged(event: { year: number; month: number }): void {
    this.loadMonthEvents(event.year, event.month);
  }

  private updateMetaTags(business: BusinessPublicInfo): void {
    const baseUrl = window.location.origin;
    const currentUrl = `${baseUrl}/${business.publicSlug}`;
    // Título principal: nombre del negocio
    const pageTitle = business.name;
    const title = `${business.name} - Reserva tu cita`;
    const description = business.description ||
      `Reserva tu cita en ${business.name}. Selecciona el servicio y horario que mejor te funcione.`;
    const imageUrl = business.logoUrl || `${baseUrl}/assets/logo.png`;

    // Título de la página: nombre del negocio
    this.title.setTitle(pageTitle);

    // Basic meta tags
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'author', content: business.name });

    // Open Graph tags (for Facebook, WhatsApp, LinkedIn, etc.)
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: currentUrl });
    this.meta.updateTag({ property: 'og:image', content: imageUrl });
    this.meta.updateTag({ property: 'og:image:width', content: '1200' });
    this.meta.updateTag({ property: 'og:image:height', content: '630' });
    this.meta.updateTag({ property: 'og:image:alt', content: business.name });
    this.meta.updateTag({ property: 'og:site_name', content: 'CitaLink Pro' });
    this.meta.updateTag({ property: 'og:locale', content: 'es_CO' });

    // Twitter Card tags
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: title });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: imageUrl });
    this.meta.updateTag({ name: 'twitter:image:alt', content: business.name });
    this.meta.updateTag({ name: 'twitter:site', content: '@citalinkpro' });

    // Additional meta tags
    this.meta.updateTag({ name: 'robots', content: 'index, follow' });
  }

  onDateChange(dateValue: string): void {
    if (!dateValue || !this.slug()) return;

    // Update the selected date signal - this will trigger timeOptions recomputation
    this.selectedDate.set(dateValue);

    const { startUtc, endUtc } = this.getDayRangeUtc(dateValue);

    // Load events for the new date - this will also trigger timeOptions recomputation
    // Set loading state while fetching events
    this.isLoadingTimeSlots.set(true);

    // Get professional ID for filtering
    const professionalId = this.selectedProfessionalId();

    this.bookingService
      .getBusinessEvents(this.slug()!, startUtc, endUtc, professionalId)
      .subscribe({
        next: (events) => {
          // Backend now filters by professional, but we still need to include events without professional (blocks)
          // Filter to include events for the selected professional OR events without professional (blocks)
          let filteredEvents = events;

          if (professionalId) {
            const normalizeId = (id: string | null | undefined): string | null => {
              if (!id) return null;
              return id.trim().toLowerCase().replace(/-/g, '');
            };

            const normalizedSelectedId = normalizeId(professionalId);
            // Include events for the selected professional OR events without professional (blocks)
            filteredEvents = events.filter(event => {
              const eventProfId = normalizeId(event.professionalId);
              return !eventProfId || eventProfId === normalizedSelectedId;
            });
          }

          this.blockedEvents.set(filteredEvents);
          // The computed timeOptions will automatically recalculate when blockedEvents changes
          // Small delay to ensure the computed has time to update
          setTimeout(() => {
            this.isLoadingTimeSlots.set(false);
          }, 100);
        },
        error: () => {
          this.blockedEvents.set([]);
          this.isLoadingTimeSlots.set(false);
        },
      });
  }

  onSubmit(): void {
    if (this.isSubmitting()) return;
    if (this.form.invalid || !this.slug() || !this.businessId()) {
      this.form.markAllAsTouched();
      this.toastService.show(
        'Revisa la información para poder enviar tu reserva.',
        'warning'
      );
      return;
    }

    const {
      serviceId,
      professionalId,
      date,
      time,
      clientName,
      phone,
      notes,
    } = this.form.getRawValue();

    if (!phone || !phone.countryId || !phone.phoneNumber) {
      this.toastService.show('El teléfono es requerido', 'warning');
      return;
    }

    const startIso = this.combineDateTime(date, time);
    if (!startIso) {
      this.toastService.show(
        'Selecciona una fecha y hora válidas.',
        'warning'
      );
      return;
    }

    const payload = {
      businessId: this.businessId()!,
      professionalId: professionalId || null,
      serviceId,
      countryId: phone.countryId,
      clientName,
      clientPhone: phone.phoneNumber.replace(/\D/g, ''),
      startDate: startIso,
      notes: notes || null,
    };

    this.isSubmitting.set(true);
    this.bookingService.createBooking(this.slug()!, payload).subscribe({
      next: (response) => {
        this.isSubmitting.set(false);

        // Calculate end date based on service duration
        const service = this.selectedService();
        const durationMinutes = service?.durationMinutes ?? 60;
        const startDate = new Date(startIso);
        const endDate = new Date(startDate.getTime() + durationMinutes * 60000);

        // Get professional name
        const professional = this.professionals().find(p => p.userId === professionalId);
        const professionalName = professional
          ? `${professional.firstName} ${professional.lastName}`
          : null;

        // Navigate to confirmation page with booking data
        this.router.navigate(['/', this.slug()!, 'confirmation'], {
          queryParams: {
            eventId: response.eventId,
            slug: this.slug()!,
            clientName: clientName,
            serviceName: service?.name || 'Servicio',
            professionalName: professionalName,
            startDate: startIso,
            endDate: endDate.toISOString(),
          }
        });
      },
      error: (err) => {
        this.isSubmitting.set(false);
        const message =
          err?.error?.message ??
          'No pudimos registrar tu reserva. Inténtalo nuevamente.';
        this.toastService.show(message, 'warning');
      },
    });
  }

  private combineDateTime(date: string, time: string): string | null {
    if (!date || !time) return null;
    const partsDate = date?.split('-').map((n: string) => Number(n)) ?? [];
    const partsTime = time?.split(':').map((n: string) => Number(n)) ?? [];
    if (partsDate.length !== 3 || partsTime.length < 2) {
      return null;
    }
    const [year, month, dayValue] = partsDate;
    const [hour, minute] = partsTime;
    const localDate = new Date(year, month - 1, dayValue, hour, minute, 0, 0);
    return localDate.toISOString();
  }

  private getDayRangeUtc(dateValue: string): { startUtc: string; endUtc: string } {
    const parts = dateValue?.split('-').map((n) => Number(n)) ?? [];
    if (parts.length !== 3) {
      const now = new Date();
      const todayStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0));
      const todayEnd = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999));
      return { startUtc: todayStart.toISOString(), endUtc: todayEnd.toISOString() };
    }
    // Local day boundaries: a UTC day would miss the evening hours in negative offsets
    const [year, month, dayValue] = parts;
    const startUtc = new Date(year, month - 1, dayValue, 0, 0, 0, 0);
    const endUtc = new Date(year, month - 1, dayValue + 1, 0, 0, 0, 0);
    return { startUtc: startUtc.toISOString(), endUtc: endUtc.toISOString() };
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getStatusName(code: string): string {
    const def = this.statusDefs[code as keyof typeof this.statusDefs];
    if (def) return def.name;
    return code;
  }

  /**
   * Converts a UTC date string to a Date object for display
   * Ensures proper timezone conversion from UTC to local time
   */
  parseUtcDate(dateStr: string): Date {
    // If the string already has timezone info, use it directly
    if (dateStr.endsWith('Z') || dateStr.includes('+') || dateStr.includes('-', 10)) {
      return new Date(dateStr);
    }
    // If no timezone info, assume UTC and append 'Z'
    return new Date(dateStr + 'Z');
  }

  private buildBasicTimeOptions(
    dateValue: string,
    service: any,
    blockedEvents: PublicAgendaEvent[],
    selectedServiceId: string | null,
    selectedProfessionalId: string | null
  ): { value: string; label: string }[] {
    // Parse selected date
    const dateParts = dateValue.split('-').map((n) => Number(n));
    if (dateParts.length !== 3) return [];

    const [year, month, day] = dateParts;
    const selectedDate = new Date(year, month - 1, day);
    const dayOfWeek = selectedDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

    // Get business schedule for this day
    const scheduleRanges = this.businessSchedule().filter(
      (range) => range.dayOfWeek === dayOfWeek && range.isActive
    );

    // If no schedule for this day, return empty array
    if (scheduleRanges.length === 0) {
      return [];
    }

    // Get service duration (default to 60 minutes)
    const serviceDuration = service?.durationMinutes ?? 60;

    // Check if we're on today's date and filter out past times
    const now = new Date();
    const isToday = selectedDate.toDateString() === now.toDateString();
    const currentMinutes = isToday ? now.getHours() * 60 + now.getMinutes() : 0;

    // Minimum interval between slots (15 minutes for better granularity)
    const minIntervalMinutes = 15;

    // Busy minutes of this day for the professional: bookings plus agenda blocks
    const eventRanges = this.getBusyRanges(blockedEvents, year, month - 1, day, selectedProfessionalId);

    // Sort events by start time
    eventRanges.sort((a, b) => a.start - b.start);

    const availableSlots: { value: string; label: string }[] = [];

    // For each schedule range, generate slots dynamically based on available gaps
    for (const range of scheduleRanges) {
      const [startHour, startMinute] = range.startTime.split(':').map(Number);
      const [endHour, endMinute] = range.endTime.split(':').map(Number);

      const rangeStartMinutes = startHour * 60 + startMinute;
      const rangeEndMinutes = endHour * 60 + endMinute;

      // Find events that overlap with this schedule range
      const rangeEvents = eventRanges.filter(
        (event) => event.end > rangeStartMinutes && event.start < rangeEndMinutes
      );

      // Build available time windows
      const availableWindows: { start: number; end: number }[] = [];

      // Start from the beginning of the schedule range (or current time if today)
      let windowStart = Math.max(rangeStartMinutes, currentMinutes);

      for (const event of rangeEvents) {
        // If there's a gap before this event, add it as an available window
        if (windowStart < event.start) {
          const windowEnd = Math.min(event.start, rangeEndMinutes);
          if (windowEnd - windowStart >= serviceDuration) {
            availableWindows.push({ start: windowStart, end: windowEnd });
          }
        }
        // Move window start to after this event
        windowStart = Math.max(windowStart, event.end);
      }

      // Add the remaining time after the last event (or from start if no events)
      if (windowStart < rangeEndMinutes) {
        const windowEnd = rangeEndMinutes;
        if (windowEnd - windowStart >= serviceDuration) {
          availableWindows.push({ start: windowStart, end: windowEnd });
        }
      }

      // Generate slots within each available window
      for (const window of availableWindows) {
        // Generate slots starting from window start, with minimum interval
        for (
          let slotMinutes = window.start;
          slotMinutes + serviceDuration <= window.end;
          slotMinutes += minIntervalMinutes
        ) {
          const hour = Math.floor(slotMinutes / 60);
          const minute = slotMinutes % 60;
          const value = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
          const label = this.formatTimeLabel(hour, minute);
          availableSlots.push({ value, label });
        }
      }
    }

    // Sort slots by time
    availableSlots.sort((a, b) => {
      const [aHour, aMin] = a.value.split(':').map(Number);
      const [bHour, bMin] = b.value.split(':').map(Number);
      return aHour * 60 + aMin - (bHour * 60 + bMin);
    });

    return availableSlots;
  }

  /**
   * Minute ranges of a local day taken by bookings or blocks that affect the professional.
   * Events for other professionals are ignored; events without a professional (business-wide
   * blocks) always apply. Events crossing midnight are clipped to the day.
   */
  private getBusyRanges(
    events: PublicAgendaEvent[],
    year: number,
    month: number,
    day: number,
    selectedProfessionalId: string | null
  ): { start: number; end: number }[] {
    const normalizeId = (id: string | null | undefined): string | null =>
      id ? id.trim().toLowerCase().replace(/-/g, '') : null;
    const selectedProfId = normalizeId(selectedProfessionalId);

    const dayStart = new Date(year, month, day, 0, 0, 0, 0).getTime();
    const dayEnd = new Date(year, month, day + 1, 0, 0, 0, 0).getTime();

    return events
      .filter((event) => {
        if (event.statusCode === 'cancelled') return false;
        const eventProfId = normalizeId(event.professionalId);
        if (!eventProfId) return true;
        // Without a selected professional only business-wide events block the agenda
        return !!selectedProfId && eventProfId === selectedProfId;
      })
      .map((event) => ({
        start: this.parseUtcDate(event.startDate).getTime(),
        end: this.parseUtcDate(event.endDate).getTime(),
      }))
      .filter((range) => range.start < dayEnd && range.end > dayStart)
      .map((range) => ({
        start: Math.round((Math.max(range.start, dayStart) - dayStart) / 60000),
        end: Math.round((Math.min(range.end, dayEnd) - dayStart) / 60000),
      }));
  }

  /** One busy event per day covered by the block, using its daily time window in local time */
  private expandBlock(block: PublicScheduleBlock): PublicAgendaEvent[] {
    const toLocalDay = (value: string) => {
      const [y, m, d] = value.slice(0, 10).split('-').map(Number);
      return new Date(y, m - 1, d);
    };
    const [startHour, startMinute] = block.startTime.split(':').map(Number);
    const [endHour, endMinute] = block.endTime.split(':').map(Number);
    if ([startHour, startMinute, endHour, endMinute].some(Number.isNaN)) return [];

    const lastDay = toLocalDay(block.endDate);
    const events: PublicAgendaEvent[] = [];

    for (let day = toLocalDay(block.startDate); day <= lastDay; day.setDate(day.getDate() + 1)) {
      const start = new Date(day);
      start.setHours(startHour, startMinute, 0, 0);
      const end = new Date(day);
      end.setHours(endHour, endMinute, 0, 0);
      // "00:00" as end time means the block runs until midnight
      if (end <= start) end.setDate(end.getDate() + 1);

      events.push({
        eventId: `${block.scheduleBlockId}-${this.formatDate(day)}`,
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        statusCode: 'blocked',
        type: 'block',
        professionalId: block.professionalId ?? null,
      });
    }

    return events;
  }

  /** Availability level of a day from its free slots vs. its capacity */
  private getAvailabilityLevel(availableSlots: number, totalSlots: number): AvailabilityLevel {
    if (totalSlots === 0 || availableSlots === 0) return 'none';
    const ratio = availableSlots / totalSlots;
    if (ratio > 0.5) return 'high';
    if (ratio > 0.2) return 'medium';
    return 'low';
  }

  private formatTimeLabel(hour24: number, minute: number): string {
    const period = hour24 >= 12 ? 'PM' : 'AM';
    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
    const minuteStr = String(minute).padStart(2, '0');
    return `${hour12}:${minuteStr} ${period}`;
  }

  // Helper to convert hex to rgba
  hexToRgba(hex: string, alpha: number): string {
    if (!hex || !hex.startsWith('#')) return `rgba(0, 0, 0, ${alpha})`;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  // Get primary color or default
  getPrimaryColor(): string {
    return this.business()?.primaryColor || '#2563eb'; // Default blue-600
  }

  // Get secondary color or default
  getSecondaryColor(): string {
    return this.business()?.secondaryColor || '#7c3aed'; // Default violet-600
  }

  // Readable text color (white or ink) to place on top of the brand color
  getBrandInk(): string {
    return getReadableInk(this.getPrimaryColor());
  }

  // Initials for the mini business header in the wizard
  getBusinessInitials(): string {
    const name = this.business()?.name || '';
    return name.split(/\s+/).slice(0, 2).map(w => w.charAt(0).toUpperCase()).join('');
  }

  // Check if business is currently open based on schedule
  isBusinessOpen = computed(() => {
    const schedule = this.businessSchedule();
    if (!schedule || schedule.length === 0) return false;

    const now = new Date();
    const currentDay = now.getDay(); // 0 = Sunday, 6 = Saturday
    const currentTime = now.getHours() * 60 + now.getMinutes(); // Current time in minutes

    const todaySchedule = schedule.filter(
      (range) => range.dayOfWeek === currentDay && range.isActive
    );

    if (todaySchedule.length === 0) return false;

    // Check if current time is within any of today's schedule ranges
    return todaySchedule.some((range) => {
      const [startHour, startMinute] = range.startTime.split(':').map(Number);
      const [endHour, endMinute] = range.endTime.split(':').map(Number);
      const startMinutes = startHour * 60 + startMinute;
      const endMinutes = endHour * 60 + endMinute;
      return currentTime >= startMinutes && currentTime < endMinutes;
    });
  });

  // Get Google Maps URL for address
  getGoogleMapsUrl(): string {
    const address = this.business()?.address;
    if (!address) return '#';
    const encodedAddress = encodeURIComponent(address);
    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
  }

  // Get today's hours summary
  getTodayHoursSummary = computed(() => {
    const schedule = this.businessSchedule();
    if (!schedule || schedule.length === 0) return null;

    const now = new Date();
    const currentDay = now.getDay(); // 0 = Sunday, 6 = Saturday

    const todaySchedule = schedule.filter(
      (range) => range.dayOfWeek === currentDay && range.isActive
    );

    if (todaySchedule.length === 0) return null;

    // Format time from HH:mm to 12-hour format
    const formatTime = (timeStr: string): string => {
      const [hour, minute] = timeStr.split(':').map(Number);
      const period = hour >= 12 ? 'p.m.' : 'a.m.';
      const hour12 = hour % 12 === 0 ? 12 : hour % 12;
      return `${hour12}:${String(minute).padStart(2, '0')} ${period}`;
    };

    // If multiple ranges, show first and last
    if (todaySchedule.length > 1) {
      const sorted = todaySchedule.sort((a, b) => a.startTime.localeCompare(b.startTime));
      const first = sorted[0];
      const last = sorted[sorted.length - 1];
      return `${formatTime(first.startTime)} – ${formatTime(last.endTime)}`;
    }

    const range = todaySchedule[0];
    return `${formatTime(range.startTime)} – ${formatTime(range.endTime)}`;
  });

  // Start booking wizard
  startBooking(serviceId: string | null = null): void {
    this.initialServiceId.set(serviceId);
    this.showWizard.set(true);
    window.scrollTo({ top: 0 });
  }

  // Handle wizard booking completion
  onBookingComplete(data: BookingWizardData): void {
    if (this.isSubmitting() || !this.slug() || !this.businessId()) return;

    const { serviceId, professionalId, date, time, clientName, phone } = data;

    if (!phone || !phone.countryId || !phone.phoneNumber) {
      this.toastService.show('El teléfono es requerido', 'warning');
      return;
    }

    const startIso = this.combineDateTime(date, time);
    if (!startIso) {
      this.toastService.show('Selecciona una fecha y hora válidas.', 'warning');
      return;
    }

    const payload = {
      businessId: this.businessId()!,
      professionalId: professionalId || null,
      serviceId,
      countryId: phone.countryId,
      clientName,
      clientPhone: phone.phoneNumber.replace(/\D/g, ''),
      startDate: startIso,
      notes: null,
    };

    this.isSubmitting.set(true);
    this.bookingService.createBooking(this.slug()!, payload).subscribe({
      next: (response) => {
        this.isSubmitting.set(false);

        // Calculate end date based on service duration
        const service = this.services().find(s => s.serviceId === serviceId);
        const durationMinutes = service?.durationMinutes ?? 60;
        const startDate = new Date(startIso);
        const endDate = new Date(startDate.getTime() + durationMinutes * 60000);

        // Get professional name
        const professional = this.professionals().find(p => p.userId === professionalId);
        const professionalName = professional
          ? `${professional.firstName} ${professional.lastName}`
          : null;

        // Navigate to confirmation page
        this.router.navigate(['/', this.slug()!, 'confirmation'], {
          queryParams: {
            eventId: response.eventId,
            slug: this.slug()!,
            clientName: clientName,
            serviceName: service?.name || 'Servicio',
            professionalName: professionalName,
            startDate: startIso,
            endDate: endDate.toISOString(),
          }
        });
      },
      error: (err) => {
        this.isSubmitting.set(false);
        const message =
          err?.error?.message ??
          'No pudimos registrar tu reserva. Inténtalo nuevamente.';
        this.toastService.show(message, 'warning');
      },
    });
  }

  // Convert services to wizard format
  getWizardServices = computed(() => {
    return this.services().map(s => ({
      serviceId: s.serviceId,
      name: s.name,
      description: s.description,
      price: s.price,
      durationMinutes: s.durationMinutes,
      imageUrl: (s as any).imageUrl ?? null,
    }));
  });

  // Convert professionals to wizard format
  getWizardProfessionals = computed(() => {
    return this.professionals().map(p => ({
      userId: p.userId,
      firstName: p.firstName,
      lastName: p.lastName,
      email: p.email,
      color: p.color,
      avatarUrl: (p as any).avatarUrl ?? null,
    }));
  });

  // Get time slots for wizard
  getWizardTimeSlots(): Array<{ value: string; label: string }> {
    // Return a new array reference to ensure Angular detects the change
    // This forces change detection when the date changes
    return [...this.timeOptions()];
  }

}
